<script lang="ts">
  // @ts-nocheck
  import { FontAwesomeIcon } from "@fortawesome/svelte-fontawesome";
  import { faCaretDown, faCaretRight, faPencil, faTrash, faChevronDown, faChevronUp, faChevronRight } from "@fortawesome/free-solid-svg-icons";
  import { Button, Toggle } from 'flowbite-svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridOptions, GridApi } from 'ag-grid-community';
  import { themeQuartz } from 'ag-grid-community';
  import { theme } from "../../services/stores/theme-store.js";
  import { onMount, tick } from 'svelte';
  import { get } from 'svelte/store';
  import ReferenceTableEditor from './table-editor/ReferenceTableEditor.svelte';
  import TopicTableEditor from './table-editor/TopicTableEditor.svelte';
  import TopicGrid from './table-editor/TopicGrid.svelte';
  import ReferenceGrid from './table-editor/ReferenceGrid.svelte';
  import { projectStructureStore, studyExtractStore, documentExtractStore, documentExtractFullStore } from './table-editor/stores.js';
  import { loadWorkingDocument } from './data/extract-data-handler.js';
  import { loadTargetExtract } from './data/target-data-handler.js';
  import { getDisplayName } from './table-editor/grouper-utils';
  import { userStore } from '../../services/stores/user-store.js';
  import { updateTargetCell, updateReferenceCell } from './table-editor/target-table-processing.js';
  import { saveTargetExtract } from './data/target-data-handler.js';
  import { getCellEditorConfig,  getLookupValueGetterSetter, getLookupCellRenderer, getLookupLabel as getLookupLabelUtil } from './table-editor/cell-editor-config.js';
  import { createCellWithIndicator, DEFAULT_CURRENT_DOCUMENT_ID } from './indicator-utils.js';
  // @ts-ignore - runtime import of TS helper via .js extension
  import { getProjectStructureUrl, getStudyExtractUrl, getDocumentWorkingUrl, getDocumentExtractUrl } from './extract-helper.js';
  import { logInfo, logError, logWarning } from "../../services/logging/log-handler.js";
  
  // Study name prop - determines which extract JSON to load
  export let studyName: string | undefined = undefined;
  
  // Current document ID for source indicator logic
  const currentDocumentID = DEFAULT_CURRENT_DOCUMENT_ID;
  
  // Indicator visibility toggle
  let showIndicators: boolean = true;

  /**
   * Normalize project structure by adding default type: "string" to fields/columns without a type
   */
  function normalizeProjectStructure(structure: any): any {
    if (!structure) return structure;
    
    const normalized = JSON.parse(JSON.stringify(structure)); // Deep clone
    
    // Ensure reference field metadata is filled in
    normalized.references?.forEach((ref: any) => {
      if (ref.fields) {
        processFields(ref.fields);
      }
      if (ref.columns) {
        processFields(ref.columns);
      }
    });
    
    // Process root-level grouper columns if they exist
    if (normalized.grouper?.columns) {
      processFields(normalized.grouper.columns);
    }
    
    const referenceLookup = normalized.references || [];
    
  normalized.topics?.forEach((topic: any) => {
    // Normalize grouper levels to expose reference definitions
    if (topic.grouper?.levels) {
      topic.grouper.levels = topic.grouper.levels.map((level: any, index: number) => {
        const levelNumber = String(level.level ?? level.number ?? index + 1);
        const refDefs = (level.refs || []).map((ref: any) => {
          const refTable = ref.refId;
          const columnId = ref.columnId;
          return {
            refTable,
            columnId,
            label: getReferenceColumnLabel(referenceLookup, refTable, columnId)
          };
        });
        
        const sources: any[] = refDefs.map((refDef: any) => ({
          type: 'reference',
          refTable: refDef.refTable,
          refType: refDef.label || refDef.refTable
        }));
        
        return {
          ...level,
          number: levelNumber,
          refDefs,
          sources
        };
      });
    }
    
    const grouperLevels = Array.isArray(topic.grouper?.levels)
      ? topic.grouper.levels.map((level: any, index: number) => ({
          definition: level,
          levelNumber: String(level.level ?? level.number ?? index + 1),
          label: level.label || `Level ${index + 1}`
        }))
      : [];
    
    const sortedLevels = [...grouperLevels].sort((a, b) => {
      const aNum = parseInt(a.levelNumber, 10);
      const bNum = parseInt(b.levelNumber, 10);
      if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
        return a.levelNumber.localeCompare(b.levelNumber);
      }
      return aNum - bNum;
    });

    if (Array.isArray(topic.tables)) {
      topic.tables = topic.tables.map((table: any) => {
        const columns = Array.isArray(table.columns) ? [...table.columns] : [];

        const primaryLevel = sortedLevels[0];
        const primaryLabel = primaryLevel?.label || 'Group';

        const hasGrouperNameColumn = columns.some(
          (col: any) => col.id === 'grouper_name'
        );
        if (!hasGrouperNameColumn) {
          columns.unshift({
            id: 'grouper_name',
            label: primaryLabel,
            refTable: 'grouper',
            refID: 'name'
          });
        }

        processFields(columns);
        return {
          ...table,
          columns
        };
      });
    }
  });
    
    return normalized;
  }
  
  /**
   * Recursively process fields/columns to add default type
   */
  function processFields(fields: any[]): void {
    if (!fields) return;
    
    fields.forEach((field: any) => {
      const isReferenceField = field.refTable || field.refID;
      if (!field.type && !isReferenceField) {
        field.type = "string";
      }
      if (field.fields) {
        processFields(field.fields);
      }
    });
  }
  
  function getReferenceColumnLabel(references: any[], refId: string, columnId: string): string {
    if (!refId) return columnId || '';
    if (refId === 'grouper') {
      return columnId === 'name' ? 'Group' : columnId || 'Group';
    }
    
    const reference = references?.find((ref: any) => ref.id === refId);
    if (!reference) {
      return columnId || refId;
    }
    
    const columns = reference.columns;
    if (Array.isArray(columns)) {
      const column = columns.find((col: any) => col.id === columnId);
      if (column?.label) return column.label;
    }
    
    const fields = reference.fields;
    if (Array.isArray(fields)) {
      const field = fields.find((fld: any) => fld.id === columnId);
      if (field?.label) return field.label;
    }
    
    if (reference.label) {
      return reference.label;
    }
    
    return columnId || refId;
  }
  
  function getPrimaryStudyName(study: any): string {
    const studyReference = study?.references?.find((ref: any) => ref.id === 'study');
    if (!studyReference) return '';
    if (Array.isArray(studyReference.values)) {
      const studyIdValue = studyReference.values.find((value: any) => value.id === 'studyid');
      if (studyIdValue?.value) {
        return studyIdValue.value;
      }
    }
    return '';
  }
  
  function normalizeStudyExtract(study: any, projectStructure: any): any {
    if (!study) return study;
    const normalized = JSON.parse(JSON.stringify(study));
    
    const topicStructureMap = new Map<string, any>();
    projectStructure?.topics?.forEach((topic: any) => {
      topicStructureMap.set(topic.id, topic);
    });
    
    normalized.topics = (normalized.topics || []).map((topic: any) => {
      const structureTopic = topicStructureMap.get(topic.id);
      const normalizedTopic: any = { ...topic };
      
      const rawGrouperData = topic.grouper ?? [];
      const grouperStructureSnapshot = structureTopic?.grouper
        ? JSON.parse(JSON.stringify(structureTopic.grouper))
        : null;
      normalizedTopic.grouperStructure = grouperStructureSnapshot;
      normalizedTopic.grouper = normalizeTopicGrouper(rawGrouperData, structureTopic);
      
      normalizedTopic.tables = (topic.tables || []).map((table: any) => {
      const normalizedRows = (table.rows || []).map((row: any) => {
        const { grouperId, ...rest } = row;
        return {
          ...rest,
          id: grouperId || row.id
        };
      });
        return {
          ...table,
          rows: normalizedRows
        };
      });
      
      return normalizedTopic;
    });
    
    return normalized;
  }
  
  function normalizeTopicGrouper(rawGrouper: any, structureTopic: any) {
    const topicId = structureTopic?.id || 'unknown';
    if (!Array.isArray(rawGrouper)) {
      throw new Error(`[grouper] Topic "${topicId}" grouper must be an array`);
    }
    const levelDefinitions = Array.isArray(structureTopic?.grouper?.levels)
      ? structureTopic.grouper.levels
      : [];
 
    const normalizedRows = rawGrouper.map((row: any, index: number) => {
      if (!row || typeof row !== 'object') {
        throw new Error(`[grouper] Topic "${topicId}" contains an invalid grouper row at index ${index}`);
      }

      if (!Array.isArray(row.levels)) {
        throw new Error(`[grouper] Topic "${topicId}" row ${index + 1} missing levels array`);
      }

      const rowId = row.grouperId ?? row.id;
      if (typeof rowId !== 'string' || !rowId.trim()) {
        throw new Error(`[grouper] Topic "${topicId}" row ${index + 1} missing grouperId`);
      }

      const normalizedLevels = levelDefinitions.map((levelDef: any, levelIndex: number) => {
        const levelNumber = String(levelDef.level ?? levelDef.number ?? levelIndex + 1);
        const rawLevel = row.levels.find(
          (lvl: any) => String(lvl.level ?? lvl.number ?? '') === levelNumber
        );

        if (!rawLevel || !Array.isArray(rawLevel.refs)) {
          throw new Error(
            `[grouper] Topic "${topicId}" row "${rowId}" level "${levelNumber}" missing refs array`
          );
        }

        const expectedRefs = Array.isArray(levelDef.refDefs) ? levelDef.refDefs.length : 0;
        if (rawLevel.refs.length !== expectedRefs) {
          throw new Error(
            `[grouper] Topic "${topicId}" row "${rowId}" level "${levelNumber}" expected ${expectedRefs} refs but received ${rawLevel.refs.length}`
          );
        }

        return {
          level: levelNumber,
          refs: rawLevel.refs.map((ref: any) => (typeof ref === 'string' ? ref : ''))
        };
      });

      const customLevel = row.levels.find(
        (lvl: any) => String(lvl.level ?? lvl.number ?? '').toLowerCase() === 'custom'
      );
      let customValue = '';
      if (typeof row.custom === 'string' && row.custom.trim()) {
        customValue = row.custom.trim();
      } else if (typeof customLevel?.value === 'string' && customLevel.value.trim()) {
        customValue = customLevel.value.trim();
      }

      if (customValue) {
        normalizedLevels.push({ level: 'custom', value: customValue });
      }

      // Preserve the cells array if it exists
      const cells = Array.isArray(row.cells) ? row.cells : [];

      return {
        grouperId: String(rowId),
        order: getNumericOrderValue(row, index + 1),
        levels: normalizedLevels,
        cells: cells
      };
    });

    return normalizedRows;
  }

  async function fetchJson(url: string): Promise<any> {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Failed to load JSON from ${url} (${response.status} ${response.statusText})`);
    }
    return response.json();
  }

  let currentDatasetKey: string = "3";

  async function initializeStores(datasetKey: string = currentDatasetKey) {
    currentDatasetKey = datasetKey;

    const projectStructureData = await fetchJson(getProjectStructureUrl(datasetKey));
    // Pass studyName to getStudyExtractUrl to select the correct extract file
    const studyExtractData = await fetchJson(getStudyExtractUrl(datasetKey, studyName));
    const documentWorkingData = await fetchJson(getDocumentWorkingUrl(datasetKey));
    const documentExtractData = await fetchJson(getDocumentExtractUrl(datasetKey));

    const normalizedStructure = normalizeProjectStructure(projectStructureData);
    projectStructureStore.set(normalizedStructure);

    const processedStudy = loadTargetExtract('study', `study_${datasetKey}_extract`, studyExtractData);
    const normalizedStudy = normalizeStudyExtract(processedStudy, normalizedStructure);
    studyExtractStore.set(normalizedStudy);

    const processedWorking = loadWorkingDocument(`doc_${datasetKey}_work`, documentWorkingData);
    documentExtractStore.set(processedWorking);

    const processedDocument = loadTargetExtract('document', `doc_${datasetKey}_extract`, documentExtractData);
    documentExtractFullStore.set(processedDocument);
  }

  // Track previous studyName to avoid unnecessary re-initialization
  let previousStudyName: string | undefined = undefined;

  // Initialize stores when component mounts or when studyName changes
  $: if (studyName !== previousStudyName) {
    previousStudyName = studyName;
    initializeStores().catch((error) => {
      logError(error, "ExtractItems2", undefined, { message: "Failed to initialize extract stores" });
    });
  }

  async function handleReset() {
    if (typeof window !== 'undefined') {
      const keysToRemove = [
        `study_${currentDatasetKey}_extract`,
        `doc_${currentDatasetKey}_extract`,
        `doc_${currentDatasetKey}_work`
      ];
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });

      const prefixes = ['workingData_', 'working_'];
      for (let index = localStorage.length - 1; index >= 0; index -= 1) {
        const key = localStorage.key(index);
        if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
          localStorage.removeItem(key);
        }
      }
    }

    initializeStores();
    await tick();
    expandState = buildDefaultExpandState();
    refreshAllGrids();
  }

  // Use stores reactively with fallbacks
  $: projectStructure = $projectStructureStore || {};
  $: studyExtract = $studyExtractStore || { topics: [] };
  
  // Lookup service for value editing (keeping for backward compatibility)
  function getLookupLabel(lookupId: string, valueId: string): string {
    return getLookupLabelUtil(projectStructure, lookupId, valueId);
  }
  
  // Helper function to get reference value by collection and ID
  function getReferenceValue(collectionId: string, itemId: string): string {
    const collection = studyExtract?.references?.find((ref: any) => ref.id === collectionId);
    if (!collection) return itemId;
    
    // Get the reference structure to find the nameRef field
    const refStructure = projectStructure?.references?.find((ref: any) => ref.id === collectionId);
    const nameRef = refStructure?.nameRef;
    
    // Handle both 'rows' structure (for tables) and 'values' structure (for objects)
    if (collection.rows) {
      const row = collection.rows.find((r: any) => r.id === itemId);
      if (row?.cells) {
        // Use nameRef if specified, otherwise fall back to 'name' or 'title'
        let nameCell;
        if (nameRef) {
          // Use the nameRef field specified in the reference structure
          nameCell = row.cells.find((c: any) => c.id === nameRef);
        } else {
          // Fallback to 'name' or 'title' for backward compatibility
          nameCell = row.cells.find((c: any) => c.id === 'name' || c.id === 'title');
        }
        return nameCell?.value || itemId;
      }
    }
    
    if (collection.values) {
      const value = collection.values.find((v: any) => v.id === itemId);
      return value?.value || itemId;
    }
    
    return itemId;
  }
  
  function ensureGrouperArrayLocal(grouper: any, context: string): any[] {
    if (!Array.isArray(grouper)) {
      throw new Error(`[grouper] Expected grouper array for ${context}`);
    }
    return grouper;
  }

  function findGrouperRowLocal(grouper: any, rowId: string, fallbackIndex?: number): any {
    const rows = ensureGrouperArrayLocal(grouper, `finding row "${rowId}"`);
    let match = rows.find((row: any) => String(row?.grouperId ?? '') === String(rowId));

    if (!match && fallbackIndex !== undefined && fallbackIndex >= 0 && fallbackIndex < rows.length) {
      match = rows[fallbackIndex];
    }

    if (!match) {
      throw new Error(`[grouper] Row "${rowId}" not found`);
    }

    return match;
  }

  function getCustomValueLocal(row: any): string {
    if (!row) return '';

    if (Array.isArray(row.levels)) {
      const customLevel = row.levels.find((lvl: any) => {
        const identifier = String(lvl.level ?? lvl.number ?? '').toLowerCase();
        return identifier === 'custom';
      });
      if (typeof customLevel?.value === 'string' && customLevel.value.trim()) {
        return customLevel.value.trim();
      }
    }
    if (typeof row.custom === 'string' && row.custom.trim()) {
      return row.custom.trim();
    }
    return '';
  }

  function getLevelRefsLocal(row: any, levelNumber: string | number): string[] {
    if (!Array.isArray(row?.levels)) {
      return [];
    }

    const target = row.levels.find(
      (lvl: any) => String(lvl.level ?? lvl.number ?? '') === String(levelNumber)
    );

    if (!target || !Array.isArray(target.refs)) {
      return [];
    }

    return target.refs.map((ref: any) => (typeof ref === 'string' ? ref : ''));
  }

  function getLevelDisplayNameLocal(row: any, levelStructure: any): string {
    if (!levelStructure) return 'Study';

    const levelNumber = String(levelStructure?.level ?? levelStructure?.number ?? '');
    const refs = getLevelRefsLocal(row, levelNumber);
    const labels: string[] = [];

    (levelStructure?.sources || [])
      .filter((source: any) => source?.type === 'reference')
      .forEach((source: any, index: number) => {
        const refId = refs[index];
        if (!refId) return;
        const refValue = getReferenceValue(source.refTable, refId);
        if (refValue && refValue !== '-') {
          labels.push(refValue);
        }
      });

    if (labels.length === 0 && levelNumber.toLowerCase() === 'custom') {
      const customValue = getCustomValueLocal(row);
      if (customValue) {
        labels.push(customValue);
      }
    }

    // If no labels found, return "Study" instead of empty string
    if (labels.length === 0) {
      return 'Study';
    }

    return labels.join(' ● ');
  }
  
  // Helper to resolve cell value from grouper table
  function getGrouperCellValue(
    grouper: any,
    rowId: string,
    columnId: string,
    grouperStructure?: any,
    rowIndex?: number
  ): string {
    const row = findGrouperRowLocal(grouper, rowId, rowIndex);
    if (!row) {
      throw new Error(`[grouper] Unable to locate grouper row "${rowId}"`);
    }

    if (columnId === 'name') {
      if (!$studyExtractStore) {
        throw new Error('[grouper] Study extract store not initialised');
      }
      if (grouperStructure) {
        return getDisplayName(row, $studyExtractStore, { grouperStructure });
      }
      return getDisplayName(row, $studyExtractStore);
    }

    if (columnId === 'custom' || columnId === 'Custom') {
      return getCustomValueLocal(row);
    }

    throw new Error(`[grouper] Unsupported grouper column "${columnId}"`);
  }
  
  // Helper to resolve cell value from any table
  function getTableCellValue(tables: any[], tableId: string, rowId: string, columnId: string): string {
    const table = tables.find((t: any) => t.id === tableId);
    if (!table?.rows) return '';
    
    const row = table.rows.find(
      (r: any) => String(r.grouperId ?? r.id ?? '') === String(rowId)
    );
    if (!row?.cells) return '';
    
    const cell = row.cells.find((c: any) => c.id === columnId);
    return cell?.value || '';
  }
  
  // Helper to resolve column reference
  function resolveColumnReference(
    column: any,
    rowId: string,
    rowIndex: number,
    grouper: any,
    tables: any[],
    options?: {
      preferredRow?: any;
      grouperStructure?: any;
    }
  ): string {
    if (!column.refTable || !column.refID) return '';

    if (column.refTable === 'grouper') {
      if (!options?.grouperStructure) {
        throw new Error('[grouper] Grouper structure required to resolve column reference');
      }

      const grouperRow =
        options?.preferredRow || findGrouperRowLocal(grouper, rowId, rowIndex);

      if (!grouperRow) {
        throw new Error(`[grouper] Unable to resolve grouper row "${rowId}" for column reference`);
      }

      if (column.refID === 'name') {
        if (!$studyExtractStore) {
          throw new Error('[grouper] Study extract store not initialised');
        }

        let displayName: string;
        
        if (column.level !== undefined && column.level !== null) {
          const levelStructure = (options.grouperStructure.levels || []).find(
            (level: any) => String(level.level ?? level.number ?? '') === String(column.level)
          );
          if (!levelStructure) {
            throw new Error(`[grouper] Level "${column.level}" not defined in grouper structure`);
          }
          displayName = getLevelDisplayNameLocal(grouperRow, levelStructure);
        } else {
          displayName = getDisplayName(grouperRow, $studyExtractStore, {
            grouperStructure: options.grouperStructure
          });
        }

        // If display name is empty or matches rowId, show "Study" instead
        const rowId = grouperRow?.grouperId || grouperRow?.id;
        const rowIdStr = rowId ? String(rowId) : '';
        
        if (!displayName || displayName === '-' || displayName === rowIdStr) {
          const customValue = getCustomValueLocal(grouperRow);
          if (customValue) {
            return customValue;
          }
          return 'Study';
        }
        
        return displayName;
      }

      if (column.refID === 'custom' || column.refID === 'Custom') {
        return getCustomValueLocal(grouperRow);
      }

      throw new Error(`[grouper] Unsupported grouper column reference "${column.refID}"`);
    }

    return getTableCellValue(tables, column.refTable, rowId, column.refID) || '';
  }
  
  // Helper to get column label from structure
  function getColumnLabel(column: any, grouper: any, tablesStructure: any): string {
    // If it's a reference column, get the label from referenced table
    if (column.refTable && column.refID) {
      if (column.refTable === 'grouper' && grouper) {
        const refColumn = grouper.columns?.find((c: any) => c.id === column.refID);
        return refColumn?.label || column.id;
      }
      // For other table references (like population.sample_size)
      if (tablesStructure) {
        const refTable = tablesStructure.find((t: any) => t.id === column.refTable);
        if (refTable) {
          const refColumn = refTable.columns?.find((c: any) => c.id === column.refID);
          return refColumn?.label || column.id;
        }
      }
      // fallback to refID
      return column.refID;
    }
    
    return column.label || column.id;
  }

  // Merge structure with extract data
  $: mergedTopics = (studyExtract?.topics || []).map((topic: any) => {
    const structureTopic = (projectStructure?.topics || []).find((st: any) => st.id === topic.id);
    
    if (!structureTopic) {
      logWarning(`No structure found for topic: ${topic.id}`, "ExtractItems2", undefined);
      return topic;
    }
    
    const topicLabel = structureTopic.label;
    
    const grouperRows = Array.isArray(topic.grouper) ? topic.grouper : [];
    const tablesData = Array.isArray(topic.tables) ? topic.tables : [];
    
    return {
      id: topic.id,
      label: topicLabel,
      grouper: grouperRows,
      grouperStructure: structureTopic.grouper,
      tables: {
        structure: structureTopic.tables,
        data: tablesData
      }
    };
  }); 
  
  // Get reference sections - reactive to store changes
  $: referenceSections = studyExtract?.references || [];
  
  function buildDefaultExpandState(): Record<string, boolean> {
    const state: Record<string, boolean> = {
      'section_references': true,
      'section_topics': true
    };

    const references = Array.isArray(referenceSections) ? referenceSections : [];
    references.forEach((ref: any) => {
      state[`ref_${ref.id}`] = false;
    });

    const topics = Array.isArray(mergedTopics) ? mergedTopics : [];
    topics.forEach((topic: any) => {
      state[`topic_${topic.id}`] = false;

      if (Array.isArray(topic.grouper)) {
        state[`grouper_${topic.id}`] = false;
      }

      if (topic.tables?.data) {
        (topic.tables.data || []).forEach((table: any) => {
          state[`table_${table.id}`] = false;
        });
      }
    });

    return state;
  }

  // State for accordion sections - Svelte 4 reactive state
  let expandState: Record<string, boolean> = buildDefaultExpandState();
  
  $: {
    const defaults = buildDefaultExpandState();
    let needsUpdate = false;
    const updatedState: Record<string, boolean> = { ...expandState };

    Object.keys(defaults).forEach((key) => {
      if (updatedState[key] === undefined) {
        updatedState[key] = defaults[key];
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      expandState = updatedState;
    }
  }
  
  function toggle(key: string) {
    // Create new object reference to trigger Svelte 4 reactivity
    expandState = {
      ...expandState,
      [key]: !expandState[key]
    };
  }
  
  // Helper to get reference label from structure
  function getReferenceLabel(refId: string): string {
    const structureRef = projectStructure?.references?.find((r: any) => r.id === refId);
    return structureRef?.label || refId;
  }
  
  // Helper to get reference structure
  function getReferenceStructure(refId: string): any {
    return projectStructure?.references?.find((r: any) => r.id === refId);
  }

  // Helper to get field label from structure
  function getFieldLabel(fieldId: string, structure: any): string {
    if (!structure?.fields) return fieldId;
    
    // Search in top-level fields
    const topLevelField = structure.fields.find((f: any) => f.id === fieldId);
    if (topLevelField) {
      return topLevelField.label || fieldId;
    }
    
    // Search in nested fields (like study_design)
    for (const field of structure.fields) {
      if (field.fields && Array.isArray(field.fields)) {
        const nestedField = field.fields.find((f: any) => f.id === fieldId);
        if (nestedField) {
          return nestedField.label || fieldId;
        }
      }
    }
    
    return fieldId;
  }

  // Helper to get table structure
  function getTableStructure(topic: any, tableId: string): any {
    return topic.tables?.structure?.find((t: any) => t.id === tableId);
  }

  // Helper to transform object data to vertical table format (label-value pairs)
  function transformObjectToVerticalTable(objectData: any, structure: any): any[] {
    if (!objectData || !structure) return [];
    
    const result: any[] = [];
    
    // Handle nested values structure
    if (objectData.values) {
      objectData.values.forEach((value: any) => {
        // Handle nested objects (like study_design)
        if (value.values && Array.isArray(value.values)) {
          value.values.forEach((nestedValue: any) => {
            result.push({
              label: getFieldLabel(nestedValue.id, structure),
              value: nestedValue.value || '-',
              id: nestedValue.id,
              reviewInfo: nestedValue.reviewInfo,
              sourceInfo: nestedValue.sourceInfo
            });
          });
        } else {
          // Handle simple values
          result.push({
            label: getFieldLabel(value.id, structure),
            value: value.value || '-',
            id: value.id,
            reviewInfo: value.reviewInfo,
            sourceInfo: value.sourceInfo
          });
        }
      });
    }
    
    return result;
  }

  // Helper to create vertical table column definitions
  function createVerticalTableColumnDefs(): any[] {
    return [
      {
        field: 'label',
        headerName: '', // Hide header
        pinned: 'left',
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        resizable: false,
        sortable: false,
        filter: false,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        suppressHeaderFocus: true,
        colId: 'label' // Add colId for CSS targeting
      },
      {
        field: 'value',
        headerName: '', // Hide header
        editable: true,
        flex: 1, // Take remaining width
        minWidth: 200,
        suppressSizeToFit: false,
        suppressAutoSize: false,
        resizable: true,
        sortable: false,
        filter: false,
        lockPosition: false,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        suppressHeaderFocus: true,
        colId: 'value', // Add colId for CSS targeting
        cellRenderer: (params: any) => {
          const value = params.value || '-';
          // For vertical tables, sourceInfo is stored directly on the row data
          const sourceInfo = params.data?.sourceInfo;
          return createCellWithIndicator(value, sourceInfo);
        },
        onCellValueChanged: (params: any) => {
          // TODO: Implement cell update logic when backend is connected
          handleCellUpdate(new CustomEvent('cellUpdate', { detail: params }));
        }
      }
    ];
  }
  
  // Event handlers for component updates
  function handleFieldUpdate(event: CustomEvent) {
    // TODO: Implement field update logic when backend is connected
  }
  
  function handleCellUpdate(event: CustomEvent) {
    // TODO: Implement cell update logic when backend is connected
  }
  
  // AG Grid configuration
  let gridApis: Map<string, GridApi> = new Map();
  
  // Track which grids have been initialized to avoid reloading
  let initializedGrids: Set<string> = new Set();
  
  // Track the hierarchy of expanded states to detect when parent sections collapse
  let previousExpandState: Record<string, boolean> = {};
  
  const defaultGridOptions: GridOptions = {
    theme: themeQuartz,
    suppressCellFocus: false, // Enable cell editing
    stopEditingWhenCellsLoseFocus: true, // Close editor when it loses focus
    suppressColumnVirtualisation: true,
    suppressRowVirtualisation: true,
    autoSizeStrategy: {
      type: 'fitCellContents',
    },
    suppressRowHoverHighlight: true,
    suppressColumnMoveAnimation: true,
    suppressContextMenu: true,
    suppressMenuHide: true,
    headerHeight: 32,
    domLayout: 'autoHeight' as const,
    enableCellSpan: true,
    // rowSelection: {
    //   enableClickSelection: false
    // },
    cellSelection: false,
    loading: false,
    // Ensure columns are displayed in the order they're defined
    suppressMovableColumns: true,
    defaultColDef: {
      editable: true, // Enable cell editing
      sortable: false,
      filter: false,
      lockPosition: true,
      suppressHeaderMenuButton: true,
      suppressHeaderContextMenu: true
    }
  };

  onMount(() => {
    // Initialize grids after DOM is ready
    setTimeout(() => {
      initializeGrids();
    }, 200);
  });

  // Note: Grid initialization is now handled by the comprehensive hierarchy tracking below

  // Sophisticated hierarchy tracking for grid management
  $: {
    // Detect when parent sections collapse (affecting child grids)
    const currentState = { ...expandState };
    const previousState = { ...previousExpandState };
    
    // Check if any parent sections collapsed
    const parentSectionsCollapsed = [
      'section_references',
      'section_topics'
    ].some(section => 
      previousState[section] === true && currentState[section] === false
    );
    
    // Check if any topic sections collapsed
    const topicSectionsCollapsed = mergedTopics.some((topic: any) => 
      previousState[`topic_${topic.id}`] === true && currentState[`topic_${topic.id}`] === false
    );
    
    // Check if any table-level accordions collapsed
    const tableAccordionsCollapsed = mergedTopics.some((topic: any) => {
      if (!topic.tables?.data) return false;
      return topic.tables.data.some((table: any) => {
        const tableKey = `table_${table.id}`;
        return previousState[tableKey] === true && currentState[tableKey] === false;
      });
    });
    
    // Check if any grouper accordions collapsed
    const grouperAccordionsCollapsed = mergedTopics.some((topic: any) => {
      const grouperKey = `grouper_${topic.id}`;
      return previousState[grouperKey] === true && currentState[grouperKey] === false;
    });
    
    // Check if any reference accordions collapsed
    const referenceAccordionsCollapsed = referenceSections.some((reference: any) => {
      const refKey = `ref_${reference.id}`;
      return previousState[refKey] === true && currentState[refKey] === false;
    });
    
    // If parent sections collapsed, clear all child grids
    if (parentSectionsCollapsed) {
      gridApis.forEach((api, key) => {
        if (api && api.destroy) {
          api.destroy();
        }
      });
      gridApis.clear();
      initializedGrids.clear();
    }
    
    // If topic sections collapsed, clear only those topic grids
    if (topicSectionsCollapsed) {
      const collapsedTopics = mergedTopics.filter((topic: any) => 
        previousState[`topic_${topic.id}`] === true && currentState[`topic_${topic.id}`] === false
      );
      
      collapsedTopics.forEach((topic: any) => {
        // Clear grouper grid
        const grouperKey = `grouper_${topic.id}`;
        if (gridApis.has(grouperKey)) {
          const api = gridApis.get(grouperKey);
          if (api && api.destroy) {
            api.destroy();
          }
          gridApis.delete(grouperKey);
          initializedGrids.delete(grouperKey);
        }
        
        // Clear table grids
        if (topic.tables?.data) {
          topic.tables.data.forEach((table: any) => {
            const tableKey = `table_${table.id}`;
            if (gridApis.has(tableKey)) {
              const api = gridApis.get(tableKey);
              if (api && api.destroy) {
                api.destroy();
              }
              gridApis.delete(tableKey);
              initializedGrids.delete(tableKey);
            }
          });
        }
      });
    }
    
    // If table accordions collapsed, clear only those specific grids
    if (tableAccordionsCollapsed) {
      mergedTopics.forEach((topic: any) => {
        if (topic.tables?.data) {
          topic.tables.data.forEach((table: any) => {
            const tableKey = `table_${table.id}`;
            if (previousState[tableKey] === true && currentState[tableKey] === false) {
              if (gridApis.has(tableKey)) {
                const api = gridApis.get(tableKey);
                if (api && api.destroy) {
                  api.destroy();
                }
                gridApis.delete(tableKey);
                initializedGrids.delete(tableKey);
              }
            }
          });
        }
      });
    }
    
    // If grouper accordions collapsed, clear only those specific grids
    if (grouperAccordionsCollapsed) {
      mergedTopics.forEach((topic: any) => {
        const grouperKey = `grouper_${topic.id}`;
        if (previousState[grouperKey] === true && currentState[grouperKey] === false) {
          if (gridApis.has(grouperKey)) {
            const api = gridApis.get(grouperKey);
            if (api && api.destroy) {
              api.destroy();
            }
            gridApis.delete(grouperKey);
            initializedGrids.delete(grouperKey);
          }
        }
      });
    }
    
    // If reference accordions collapsed, clear only those specific grids
    if (referenceAccordionsCollapsed) {
      referenceSections.forEach((reference: any) => {
        const refKey = `ref_${reference.id}`;
        if (previousState[refKey] === true && currentState[refKey] === false) {
          if (gridApis.has(refKey)) {
            const api = gridApis.get(refKey);
            if (api && api.destroy) {
              api.destroy();
            }
            gridApis.delete(refKey);
            initializedGrids.delete(refKey);
          }
        }
      });
    }
    
    // Update previous state
    previousExpandState = { ...currentState };
    
    // Now handle expansions with proper timing
    if (expandState['section_references']) {
      setTimeout(() => {
        initializeReferenceGrids();
      }, 200);
    }
    
    mergedTopics.forEach((topic: any) => {
      if (expandState[`topic_${topic.id}`]) {
        setTimeout(() => {
          initializeTopicGrids(topic);
        }, 200);
      }
    });
    
    // Handle individual table accordion expansions
    mergedTopics.forEach((topic: any) => {
      if (topic.tables?.data) {
        topic.tables.data.forEach((table: any) => {
          const tableKey = `table_${table.id}`;
          if (expandState[tableKey]) {
            setTimeout(() => {
              initializeTopicGrids(topic);
            }, 200);
          }
        });
      }
    });
    
    // Handle individual grouper accordion expansions
    mergedTopics.forEach((topic: any) => {
      const grouperKey = `grouper_${topic.id}`;
      if (expandState[grouperKey]) {
        setTimeout(() => {
          initializeTopicGrids(topic);
        }, 200);
      }
    });
    
    // Handle individual reference accordion expansions
    referenceSections.forEach((reference: any) => {
      const refKey = `ref_${reference.id}`;
      if (expandState[refKey]) {
        setTimeout(() => {
          initializeReferenceGrids();
        }, 200);
      }
    });
  }

  // Track previous studyExtract to detect data changes
  let previousStudyExtractKey: string = '';
  
  // Refresh grouper grids when underlying data changes (e.g., when studyName changes)
  $: {
    // Create a simple key to detect when studyExtract data actually changes
    // This avoids expensive deep comparisons while still detecting meaningful changes
    const currentTopics = mergedTopics || [];
    const currentKey = currentTopics.map((t: any) => {
      const grouperRows = Array.isArray(t.grouper) ? t.grouper : [];
      return `${t.id}:${grouperRows.length}:${grouperRows.map((r: any) => {
        const rowId = r?.grouperId || r?.id || '';
        const cells = Array.isArray(r?.cells) ? r.cells : [];
        const cellValues = cells.map((c: any) => `${c.id}=${c.value || ''}`).join('|');
        return `${rowId}[${cellValues}]`;
      }).join(';')}`;
    }).join('||');
    
    // Only update if the key actually changed (data changed)
    if (currentKey && currentKey !== previousStudyExtractKey) {
      previousStudyExtractKey = currentKey;
      
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        // Update all initialized grouper grids with latest data
        currentTopics.forEach((topic: any) => {
          const grouperKey = `grouper_${topic.id}`;
          const hasGrouperStructure = Array.isArray(topic.grouperStructure?.levels);
          const grouperRows = Array.isArray(topic.grouper) ? topic.grouper : [];
          
          // Only update if grid is initialized, accordion is expanded, and we have structure
          if (initializedGrids.has(grouperKey) && expandState[grouperKey] && hasGrouperStructure) {
            const gridApi = gridApis.get(grouperKey);
            if (gridApi) {
              try {
                const levelDefs = topic.grouperStructure.levels || [];
                const isMultiLevel = levelDefs.length > 1;
                const structureColumns = levelDefs;
                
                // Generate new row data from latest grouper data
                let rowData = createGrouperRowData(
                  grouperRows,
                  structureColumns,
                  grouperRows,
                  [],
                  isMultiLevel,
                  topic.grouperStructure,
                  studyExtract
                );
                
                // Final deduplication pass (same as in initializeTopicGrids)
                const seen = new Map<string, any>();
                rowData = rowData.filter((row: any) => {
                  const keyParts = [row.group || ''];
                  if (row.levelDisplays && Array.isArray(row.levelDisplays)) {
                    keyParts.push(row.levelDisplays.join('|'));
                  }
                  if (row.custom) {
                    keyParts.push(row.custom);
                  }
                  const key = keyParts.join('||');
                  
                  if (seen.has(key)) {
                    return false;
                  }
                  seen.set(key, row);
                  return true;
                });
                
                // Update the grid's rowData
                gridApi.setGridOption('rowData', rowData);
              } catch (error) {
                logError(error, "ExtractItems2", undefined, { message: `Error updating grouper grid ${grouperKey}` });
              }
            }
          }
        });
      }, 100);
    }
  }
  
  // Track editor state to refresh grids when editor closes
  let previousEditorStateForRefresh = false;
  let previousGrouperEditorState = false;
  $: {
    const currentEditorState = showReferenceTableEditor || showTopicTableEditor;
    const editorJustClosed = previousEditorStateForRefresh && !currentEditorState;
    
    // Refresh reference grids when editor just closed
    if (editorJustClosed && referenceSections) {
      // Small delay to ensure store has been updated
      setTimeout(() => {
        // Get latest data from store to ensure we have the most up-to-date reference data
        const latestStudyExtract = $studyExtractStore;
        const latestReferences = latestStudyExtract?.references || [];
        
        // Update all initialized reference grids with latest data
        latestReferences.forEach((reference: any) => {
          const refKey = `ref_${reference.id}`;
          // Only update if grid is initialized and accordion is expanded
          if (initializedGrids.has(refKey) && expandState[refKey]) {
            const gridApi = gridApis.get(refKey);
            if (gridApi) {
              try {
                const refStructure = getReferenceStructure(reference.id);
                let rowData: any[] = [];
                
                if (refStructure?.type === 'table') {
                  const columns = refStructure.columns || [];
                  rowData = createReferenceRowData(reference.rows || [], columns);
                } else if (refStructure?.type === 'object') {
                  rowData = transformObjectToVerticalTable(reference, refStructure);
                }
                
                // Use setRowData method which is the recommended way to replace all row data
                // This works correctly when rows have IDs (which we now ensure in createReferenceRowData)
                if (rowData.length > 0) {
                  gridApi.setRowData(rowData);
                } else {
                  // Clear grid if no data
                  gridApi.setRowData([]);
                }
              } catch (error) {
                logError(error, "ExtractItems2", undefined, { message: `Error updating reference grid ${refKey}` });
                // Fallback: try setGridOption if setRowData fails
                try {
                  const refStructure = getReferenceStructure(reference.id);
                  let rowData: any[] = [];
                  
                  if (refStructure?.type === 'table') {
                    const columns = refStructure.columns || [];
                    rowData = createReferenceRowData(reference.rows || [], columns);
                  } else if (refStructure?.type === 'object') {
                    rowData = transformObjectToVerticalTable(reference, refStructure);
                  }
                  
                  gridApi.setGridOption('rowData', rowData);
                  } catch (fallbackError) {
                    logError(fallbackError, "ExtractItems2", undefined, { message: `Fallback update also failed for reference grid ${refKey}` });
                }
              }
            }
          }
        });
      }, 100);
    }
    
    // Refresh topic table grids when editor just closed
    if (editorJustClosed && mergedTopics) {
      // Small delay to ensure store has been updated
      setTimeout(() => {
        // Update all initialized topic table grids with latest data
        mergedTopics.forEach((topic: any) => {
          if (topic.tables?.data && topic.tables.data.length > 0) {
            topic.tables.data.forEach((table: any) => {
              const tableKey = `table_${table.id}`;
              // Only update if grid is initialized and accordion is expanded
              if (initializedGrids.has(tableKey) && expandState[tableKey]) {
                const gridApi = gridApis.get(tableKey);
                if (gridApi) {
                  try {
                    const tableStructure = topic.tables.structure?.find((t: any) => t.id === table.id);
                    if (tableStructure) {
                      // Regenerate row data with latest data from store
                    const grouperRows = Array.isArray(topic.grouper) ? topic.grouper : [];
                    const rowData = createTopicRowData(
                      table.rows || [],
                      tableStructure.columns || [],
                      grouperRows,
                      topic.tables.data,
                      false,
                      topic.grouperStructure
                    );
                      
                      // Update the grid's rowData
                      gridApi.setGridOption('rowData', rowData);
                    }
                  } catch (error) {
                    logError(error, "ExtractItems2", undefined, { message: `Error updating topic table grid ${tableKey}` });
                  }
                }
              }
            });
          }
        });
      }, 100);
    }
    
    previousEditorStateForRefresh = currentEditorState;
  }
  
  // Refresh grouper grids when GrouperEditor closes
  $: {
    const grouperEditorJustClosed = previousGrouperEditorState && !showGrouperEditor;
    
    // Refresh grouper grids when editor just closed
    if (grouperEditorJustClosed && mergedTopics) {
      // Small delay to ensure store has been updated
      setTimeout(() => {
        // Update all initialized grouper grids with latest data
        mergedTopics.forEach((topic: any) => {
          const grouperKey = `grouper_${topic.id}`;
          const hasGrouperStructure = Array.isArray(topic.grouperStructure?.levels);
          const grouperRows = Array.isArray(topic.grouper) ? topic.grouper : [];
          // Only update if grid is initialized, accordion is expanded, and we have structure + rows
          if (initializedGrids.has(grouperKey) && expandState[grouperKey] && hasGrouperStructure && grouperRows.length >= 0) {
            const gridApi = gridApis.get(grouperKey);
            if (gridApi) {
              try {
                const levelDefs = topic.grouperStructure.levels || [];
                const isMultiLevel = levelDefs.length > 1;
                const structureColumns = levelDefs;
                
                // Generate new row data from latest grouper data
                let rowData = createGrouperRowData(
                  grouperRows,
                  structureColumns,
                  grouperRows,
                  [],
                  isMultiLevel,
                  topic.grouperStructure,
                  studyExtract
                );
                
                // Final deduplication pass (same as in initializeTopicGrids)
                const seen = new Map<string, any>();
                rowData = rowData.filter((row: any) => {
                  const keyParts = [row.group || ''];
                  if (row.levelDisplays && Array.isArray(row.levelDisplays)) {
                    keyParts.push(row.levelDisplays.join('|'));
                  }
                  if (row.custom) {
                    keyParts.push(row.custom);
                  }
                  const key = keyParts.join('||');
                  
                  if (seen.has(key)) {
                    return false;
                  }
                  seen.set(key, row);
                  return true;
                });
                
                // Update the grid's rowData
                gridApi.setGridOption('rowData', rowData);
              } catch (error) {
                logError(error, "ExtractItems2", undefined, { message: `Error updating grouper grid ${grouperKey}` });
              }
            }
          }
          
          // Also refresh all topic table grids for this topic when grouper changes
          // This ensures that deleted grouper rows are removed from topic tables
          if (topic.tables?.data && topic.tables.data.length > 0) {
            topic.tables.data.forEach((table: any) => {
              const tableKey = `table_${table.id}`;
              // Only update if grid is initialized and accordion is expanded
              if (initializedGrids.has(tableKey) && expandState[tableKey]) {
                const gridApi = gridApis.get(tableKey);
                if (gridApi) {
                  try {
                    const tableStructure = topic.tables.structure?.find((t: any) => t.id === table.id);
                    if (tableStructure) {
                      // Regenerate row data with latest grouper data
                      const rowData = createTopicRowData(
                        table.rows || [],
                        tableStructure.columns || [],
                        grouperRows,
                        topic.tables.data,
                        false,
                        topic.grouperStructure
                      );
                      
                      // Update the grid's rowData
                      gridApi.setGridOption('rowData', rowData);
                    }
                  } catch (error) {
                    logError(error, "ExtractItems2", undefined, { message: `Error updating topic table grid ${tableKey}` });
                  }
                }
              }
            });
          }
        });
      }, 100);
    }
    
    previousGrouperEditorState = showGrouperEditor;
  }

  // Function to initialize a specific grid
  function initializeGrid(gridKey: string, elementId: string, gridOptions: any, retryCount: number = 0) {
    const element = document.querySelector(elementId) as HTMLElement;
    if (element) {
      // Always create new grid if element exists and we don't have a functional one
      const existingApi = gridApis.get(gridKey);
      
      if (existingApi) {
        try {
          // Test if the grid is still functional
          existingApi.getGridOption('rowData');
          return;
        } catch (e) {
          // Clean up the broken grid
          if (existingApi.destroy) {
            existingApi.destroy();
          }
          gridApis.delete(gridKey);
          initializedGrids.delete(gridKey);
        }
      }
      
      // Create new grid
      try {
        const api = createGrid(element, gridOptions);
        gridApis.set(gridKey, api);
        initializedGrids.add(gridKey);
      } catch (error) {
        logError(error, "ExtractItems2", undefined, { message: `Error creating grid ${gridKey}` });
        // Don't mark as initialized if creation failed
      }
    } else {
      // Element not found - retry up to 5 times with increasing delays
      if (retryCount < 5) {
        const delay = (retryCount + 1) * 100; // 100ms, 200ms, 300ms, 400ms, 500ms
        setTimeout(() => {
          initializeGrid(gridKey, elementId, gridOptions, retryCount + 1);
        }, delay);
      } else {
        logWarning(`Element ${elementId} not found for grid ${gridKey} after 5 retries`, "ExtractItems2", undefined);
      }
    }
  }

  // Initialize reference grids when references section is expanded
  // Note: Table-type references now use ReferenceGrid component, so we only need to initialize object-type references
  function initializeReferenceGrids() {
    referenceSections.forEach((reference: any) => {
      const refKey = `ref_${reference.id}`;
      // Only initialize grids for references that are actually expanded
      if (!expandState[refKey]) {
        return;
      }
      
      const refStructure = getReferenceStructure(reference.id);
      const gridKey = `ref_${reference.id}`;
      const elementId = `#refGrid_${reference.id}`;
      
      // Skip table-type references - they use ReferenceGrid component now
      if (refStructure?.type === 'table') {
        return;
      }
      
      // Only initialize object-type references manually
      if (refStructure?.type === 'object' && !initializedGrids.has(gridKey)) {
        
        // Create vertical table for object types
        const columnDefs = createVerticalTableColumnDefs();
        const rowData = transformObjectToVerticalTable(reference, refStructure);
        
        const refGridOptions = {
          columnDefs: columnDefs,
          rowData: rowData,
          getRowId: (params: any) => params.data?.id || params.node?.id, // Use row id for row identification
          suppressLoadingOverlay: true,
          suppressCellFocus: false, // Enable cell editing
          suppressColumnVirtualisation: true,
          suppressRowVirtualisation: true,
          suppressRowHoverHighlight: true,
          suppressColumnMoveAnimation: true,
          suppressContextMenu: true,
          suppressMenuHide: true,
          headerHeight: 0, // Hide header for object types
          domLayout: 'autoHeight', // Keep autoHeight for grid to display properly
          // Calculate row height based on multiline content length to prevent flashing
          getRowHeight: (params: any) => {
            let maxHeight = 32; // Base row height for non-multiline rows
            
            // Check each column for multiline content and calculate required height
            columnDefs.forEach((colDef: any) => {
              if (colDef.wrapText && params.data && colDef.field) {
                const value = params.data[colDef.field];
                if (value && value !== '-' && typeof value === 'string') {
                  // Estimate height based on content length and column width
                  // Assuming average character width of ~8px and column width of 200px
                  const columnWidth = colDef.width || 200;
                  const charsPerLine = Math.floor(columnWidth / 8);
                  const lines = Math.ceil(value.length / charsPerLine);
                  // Add padding (16px top + bottom) and line height (1.2em ≈ 19px per line)
                  const estimatedHeight = 16 + (lines * 19);
                  maxHeight = Math.max(maxHeight, estimatedHeight);
                }
              }
            });
            
            // Return calculated height - this prevents continuous recalculation
            // by using a fixed calculation based on content, not dynamic measurement
            return Math.min(maxHeight, 300); // Cap at 300px to prevent excessive height
          },
          enableCellSpan: true,
          cellSelection: false,
          loading: false,
          suppressMovableColumns: true,
          suppressRowTransform: false,
          autoSizeStrategy: {
            type: 'fitCellContents',
          },
          defaultColDef: {
            editable: true, // Enable cell editing
            resizable: false,
            sortable: false,
            filter: false,
            lockPosition: true,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            wrapText: false, // Only enable for multiline columns
            autoHeight: false, // Only enable for multiline columns to prevent flashing
          },
          onCellValueChanged: (params: any) => {
            // Handle direct cell editing in ExtractItems2 for object-type references
            // (Table-type references are handled by ReferenceGrid component)
            // This would need to handle object-type reference updates if needed
          }
        };
        
        initializeGrid(gridKey, elementId, refGridOptions);
      } else if (!refStructure || refStructure.type !== 'object') {
        logWarning(`Unknown reference type: ${refStructure?.type}`, "ExtractItems2", undefined);
      }
    });
  }

  // Initialize topic grids when topic section is expanded
  function initializeTopicGrids(topic: any) {
    const grouperRows = Array.isArray(topic.grouper) ? topic.grouper : [];
    const grouperStructure = topic.grouperStructure;

    // Initialize grouper grid
    if (grouperStructure && Array.isArray(grouperStructure.levels)) {
      const grouperKey = `grouper_${topic.id}`;
      // Only initialize grids for grouper accordions that are actually expanded
      if (expandState[grouperKey] && !initializedGrids.has(grouperKey)) {
        const gridKey = `grouper_${topic.id}`;
        const elementId = `#grouperGrid_${topic.id}`;
        
        const structureColumns = grouperStructure.levels || [];
        const isMultiLevel = structureColumns.length > 1;
        const columnDefs = createTopicColumnDefs(structureColumns, grouperStructure, isMultiLevel);
        
        let rowData = createGrouperRowData(
          grouperRows,
          structureColumns,
          grouperRows,
          [],
          isMultiLevel,
          grouperStructure,
          studyExtract
        );
        
        // Debug: Log first row data to check if grouper columns are populated
        if (rowData.length > 0) {
          const firstRow = rowData[0];
          const currentProjectStructure = get(projectStructureStore) || {};
          const rootGrouperColumns = currentProjectStructure?.grouper?.columns || [];
        }
        
        // Final deduplication pass - remove any duplicates based on group + level values + custom
        const seen = new Map<string, any>();
        rowData = rowData.filter((row: any) => {
          const keyParts = [row.group || ''];
          if (Array.isArray(row.levelDisplays)) {
            keyParts.push(row.levelDisplays.join('|'));
          }
          if (row.custom) {
            keyParts.push(row.custom);
          }
          
          const key = keyParts.join('||');
          
          if (seen.has(key)) {
            logWarning(`[initializeTopicGrids] Removing duplicate row: ${key}`, "ExtractItems2", undefined);
            return false;
          }
          seen.set(key, row);
          return true;
        });
        
        
        // Verify column definitions include grouper columns
        const grouperColumnFields = columnDefs
          .flatMap((col: any) => col.children ? col.children.map((c: any) => c.field) : [col.field])
          .filter((field: string) => {
            const currentProjectStructure = get(projectStructureStore) || {};
            const rootGrouperColumns = currentProjectStructure?.grouper?.columns || [];
            return rootGrouperColumns.some((c: any) => c.id === field);
          });
        
        const grouperGridOptions = {
          ...defaultGridOptions,
          columnDefs: columnDefs,
          rowData: rowData,
          defaultColDef: {
            editable: false, // Grouper table is read-only in ExtractItems2 - editing is done in GrouperEditor
            sortable: false,
            filter: false,
            lockPosition: true,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true
          },
          onGridReady: (params: any) => {
            // Auto-size the grouper columns (sample_size, analysis_set, etc.) based on content
            const currentProjectStructure = get(projectStructureStore) || {};
            const rootGrouperColumns = currentProjectStructure?.grouper?.columns || [];
            if (rootGrouperColumns.length > 0) {
              const grouperColumnIds = rootGrouperColumns.map((col: any) => col.id);
              params.api.autoSizeColumns(grouperColumnIds);
            }
          }
          // Removed onCellValueChanged - grouper table is read-only
        };
        
        initializeGrid(gridKey, elementId, grouperGridOptions);
        // Note: initializedGrids is now managed inside initializeGrid after successful creation
      }
    }

    // Table grids are now handled by TopicGrid component - no manual initialization needed
  }

  function initializeGrids(forceRefresh: boolean = false) {
    
    if (forceRefresh) {
      // Clear all existing grids
      gridApis.forEach((api, key) => {
        if (api && api.destroy) {
          api.destroy();
        }
      });
      gridApis.clear();
      initializedGrids.clear();
    }
    
    // Initialize grids for currently visible sections
    if (expandState['section_references']) {
      initializeReferenceGrids();
    }
    
    mergedTopics.forEach((topic: any) => {
      if (expandState[`topic_${topic.id}`]) {
        initializeTopicGrids(topic);
      }
    });
  }

  // Function to refresh all grids (for manual refresh)
  function refreshAllGrids() {
    
    // Clear existing grids
    gridApis.forEach((api, key) => {
      if (api && api.destroy) {
        api.destroy();
      }
    });
    gridApis.clear();
    initializedGrids.clear();
    
    // Reinitialize after a short delay with force refresh
    setTimeout(() => {
      initializeGrids(true);
    }, 100);
  }

  /* TOPIC TABLES */
  // Helper to create AG Grid column definitions
  function createTopicColumnDefs(columns: any[], grouperStructure?: any, isMultiLevel: boolean = false): any[] {
    // Handle grouper tables using the simplified schema
    if (grouperStructure?.levels) {
      const levelEntries = (grouperStructure.levels || []).map((level: any, index: number) => ({
        levelNumber: String(level.level ?? level.number ?? index + 1),
        label: level.label || level.id || `Level ${index + 1}`
      }));

      const sortedLevels = [...levelEntries].sort((a, b) => {
        const aNum = parseInt(a.levelNumber, 10);
        const bNum = parseInt(b.levelNumber, 10);
        if (Number.isNaN(aNum) || Number.isNaN(bNum)) {
          return a.levelNumber.localeCompare(b.levelNumber);
        }
        return aNum - bNum;
      });

      const allColumns: any[] = [
        {
          field: 'row',
          headerName: 'ROW',
          pinned: 'left',
          width: 60,
          minWidth: 60,
          maxWidth: 60,
          editable: false,
          cellRenderer: (params: any) => params.value ?? '-',
          sortable: false,
          filter: false,
          resizable: false,
          lockPosition: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          cellStyle: {
            padding: '8px 8px',
            'text-align': 'center'
          }
        },
      ];

      const primaryLabel = sortedLevels[0]?.label || 'Group';
      allColumns.push({
        field: 'group',
        headerName: primaryLabel.toUpperCase(),
        pinned: 'left',
        width: 220,
        minWidth: 180,
        editable: false,
        cellRenderer: (params: any) => params.value ?? '-',
        sortable: false,
        filter: false,
        resizable: false,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        cellStyle: {
          padding: '8px 8px'
        }
      });

      sortedLevels.slice(1).forEach((levelEntry) => {
        const fieldName = `level_${levelEntry.levelNumber}`;
        allColumns.push({
          field: fieldName,
          headerName: (levelEntry.label || `Level ${levelEntry.levelNumber}`).toUpperCase(),
          pinned: 'left',
          width: 200,
          minWidth: 180,
          editable: false,
          cellRenderer: (params: any) => params.value ?? '-',
          sortable: false,
          filter: false,
          resizable: false,
          lockPosition: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          cellStyle: {
            padding: '8px 8px'
          }
        });
      });

      // Add root-level grouper columns (e.g., sample_size, analysis_set, etc.)
      const currentProjectStructure = get(projectStructureStore) || {};
      const rootGrouperColumns = currentProjectStructure?.grouper?.columns || [];
      
      rootGrouperColumns.forEach((columnDef: any) => {
        const lookupRenderer = getLookupCellRenderer(columnDef, currentProjectStructure);
        allColumns.push({
          field: columnDef.id,
          headerName: (columnDef.label || columnDef.id).toUpperCase(),
          // Let columns auto-size based on content (no fixed width)
          minWidth: 100,
          editable: false,
          cellRenderer: (params: any) => {
            let displayValue = params.value || '-';
            if (lookupRenderer && displayValue !== '-') {
              displayValue = lookupRenderer(params) || '-';
            }
            return displayValue;
          },
          sortable: false,
          filter: false,
          resizable: false, // Columns should not be resizable
          lockPosition: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          cellStyle: {
            padding: '8px 8px'
          }
        });
      });

      return allColumns;
    }
    
    // Process regular table columns (fallback for all tables)
    return columns.map((column, index) => {
      const isFixed = index === 0;
      const isMultiline = column.multiline === true;
      
      // Handle GROUPER_NAME column - use defined label if available, otherwise look up from grouper table
      let headerName = column.label || column.id;
      if (column.id === 'grouper_name') {
        // If label is defined, use it; otherwise look up from grouper table
        if (column.label) {
          headerName = column.label;
        } else if (grouperStructure) {
          const grouperNameColumn = grouperStructure.columns?.find((c: any) => c.id === 'name');
          headerName = grouperNameColumn?.label || 'Name';
        }
      }
      
      // Check if this is a grouper reference column
      const isGrouperColumn = column.refTable === 'grouper' || column.id === 'grouper_name';
      
      // Handle column grouping
      let children = undefined;
      if (column.type === 'group' && column.fields) {
        children = column.fields.map((field: any) => {
          const isGrouperField = field.refTable === 'grouper' || field.id === 'grouper_name';
          const fieldMultiline = field.multiline === true;
          const editorConfig = isGrouperField ? { cellEditor: undefined, cellEditorPopup: undefined, cellEditorParams: undefined } : getCellEditorConfig(field, projectStructure);
          
          return {
            field: field.id,
            headerName: field.label || field.id,
            minWidth: fieldMultiline ? 200 : undefined,
            width: fieldMultiline ? 200 : undefined,
            maxWidth: fieldMultiline ? 200 : undefined,
            editable: !isGrouperField, // Grouper columns are read-only
            ...editorConfig,
            sortable: false,
            filter: false,
            resizable: false,
            lockPosition: true,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            cellRenderer: (params: any) => {
              let displayValue = params.value || '-';
              // For lookup fields, display the label instead of the ID
              const lookupRenderer = getLookupCellRenderer(field, projectStructure);
              if (lookupRenderer && displayValue !== '-') {
                displayValue = lookupRenderer(params) || '-';
              }
              const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
              return createCellWithIndicator(displayValue, sourceInfo, currentDocumentID);
            },
            cellStyle: fieldMultiline ? {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px'
            } : {
              'padding': '8px 8px'
            }
          };
        });
      }
      
      const editorConfig = isGrouperColumn ? { cellEditor: undefined, cellEditorPopup: undefined, cellEditorParams: undefined } : getCellEditorConfig(column, projectStructure);
      
      // For lookup columns, get value getter/setter configuration
      const lookupValueConfig = isGrouperColumn ? null : getLookupValueGetterSetter(column, projectStructure);
      
      const columnDef: any = {
        field: column.id,
        headerName: headerName,
        pinned: isFixed ? 'left' : undefined,
        width: isMultiline ? 200 : (isFixed && index === 0 ? 200 : undefined),
        minWidth: isMultiline ? 200 : (isFixed && index === 0 ? 200 : undefined),
        maxWidth: isMultiline ? 200 : (isFixed && index === 0 ? 200 : undefined),
        editable: !isGrouperColumn, // Grouper columns are read-only
        ...editorConfig,
        // Convert "-" to "" for editing, "" to "-" for display
        // For lookup columns, use the shared utility's valueGetter/valueSetter
        valueGetter: (params: any) => {
          const value = params.data[column.id];
          if (value === '-' || value === undefined || value === null) return '';
          
          // Use lookup value getter if available
          if (lookupValueConfig?.valueGetter) {
            return lookupValueConfig.valueGetter(params);
          }
          
          return value;
        },
        valueSetter: (params: any) => {
          let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
          
          // Use lookup value setter if available
          if (lookupValueConfig?.valueSetter) {
            return lookupValueConfig.valueSetter(params);
          }
          
          params.data[column.id] = value;
          return true;
        },
        cellRenderer: (params: any) => {
          let displayValue = params.value || '-';
          // For lookup fields, display the label instead of the ID
          const lookupRenderer = getLookupCellRenderer(column, projectStructure);
          if (lookupRenderer && displayValue !== '-') {
            displayValue = lookupRenderer(params) || '-';
          }
          const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
          return createCellWithIndicator(displayValue, sourceInfo, currentDocumentID);
        },
        cellClassRules: {
          'ag-row-spanning-cell': (params: any) => {
            return params.data?.rowSpan > 0;
          }
        },
        suppressSizeToFit: isFixed,
        suppressAutoSize: isFixed,
        sortable: false,
        filter: false,
        resizable: false,
        lockPosition: true,
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        cellStyle: isMultiline || isFixed ? { 
          'white-space': 'normal',
          'word-wrap': 'break-word',
          'overflow-wrap': 'break-word',
          'padding': '8px 8px'
        } : {
          'padding': '8px 8px'
        }
      };
      
      // Add children for column grouping
      if (children) {
        columnDef.children = children;
      }
      
      return columnDef;
    });
  }

  // Helper to create AG Grid row data
  function createTopicRowData(
    rows: any[],
    columns: any[],
    grouperRows?: any[],
    tablesData?: any[],
    isMultiLevel: boolean = false,
    grouperStructure?: any
  ): any[] {
    // Check if any row has levels structure (for both baseline and efficacy groupers)
    const hasLevels = rows.some(row => row.levels && row.levels.length > 0);
    
    if (isMultiLevel || hasLevels) {
      return createMultiLevelRowData(rows, columns, tablesData, grouperStructure);
    }
    
    const grouperOrderMap = new Map<string, number>();
    const grouperArray = Array.isArray(grouperRows) ? grouperRows : [];

    grouperArray.forEach((gRow: any, idx: number) => {
      const order = getNumericRowValue(gRow, idx + 1);
      const key = String(gRow?.grouperId ?? gRow?.id ?? '');
      if (key) {
        grouperOrderMap.set(key, order);
      }
    });

    const sortedRows = [...rows].sort((a: any, b: any) => {
      const orderA = grouperOrderMap.get(a?.grouper_row_id || a?.grouperRowId || a?.id) ?? Number.MAX_SAFE_INTEGER;
      const orderB = grouperOrderMap.get(b?.grouper_row_id || b?.grouperRowId || b?.id) ?? Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return (a?.id || '').localeCompare(b?.id || '');
    });

    return sortedRows.map((row, rowIndex) => {
       const fallbackIndex = rowIndex + 1;
       const rowOrder = grouperOrderMap.get(row?.grouper_row_id || row?.grouperRowId || row?.id) ?? fallbackIndex;
       const rowIdentifier = String(row?.id ?? row?.grouperId ?? `row_${fallbackIndex}`);
       const rowData: any = { id: rowIdentifier, row: rowOrder };
      
      columns.forEach(column => {
        const cell = row.cells?.find((c: any) => c.id === column.id);
        
        if (cell?.ref && cell?.refID) {
          // Handle cell references (like in grouper table)
          rowData[column.id] = getReferenceValue(cell.ref, cell.refID);
          // Store sourceInfo for reference cells if available
          if (cell.sourceInfo) {
            rowData[`${column.id}_sourceInfo`] = cell.sourceInfo;
          }
        } else if (column.refTable) {
          // Handle column references (like grouper_name in data tables)
          rowData[column.id] = resolveColumnReference(
            column,
            row.grouper_row_id || row.grouperRowId || row.id,
            rowIndex,
            grouperArray,
            tablesData || [],
            grouperStructure
          );
          // Note: sourceInfo for reference columns may not be available in the same way
        } else if (column.type === 'group' && column.fields) {
          // Handle column grouping - map individual cells to grouped fields
          column.fields.forEach((field: any) => {
            const fieldCell = row.cells?.find((c: any) => c.id === field.id);
            if (fieldCell) {
              rowData[field.id] = fieldCell.value || '-';
              // Store sourceInfo for grouped fields if available
              if (fieldCell.sourceInfo) {
                rowData[`${field.id}_sourceInfo`] = fieldCell.sourceInfo;
              }
            }
          });
        } else {
          // Handle regular cell data
          rowData[column.id] = cell?.value || '-';
          // Store sourceInfo if available
          if (cell?.sourceInfo) {
            rowData[`${column.id}_sourceInfo`] = cell.sourceInfo;
          }
        }
      });
      
      return rowData;
    });
  }

  /* REFERENCE TABLES */
  // Helper functions for reference tables
  function createReferenceColumnDefs(columns: any[]) {
    
    const columnDefs: any[] = [];
    
    columns.forEach((column, index) => {
      const isFirstColumn = index === 0;
      const isFixed = isFirstColumn;
      const isMultiline = column.multiline === true;
      
      // Handle grouped columns (e.g., study_design with fields: blinding, stratification_factors)
      if (column.type === 'group' && Array.isArray(column.fields)) {
        const groupColumns = column.fields.map((field: any) => {
          const fieldMultiline = field.multiline === true || field.type === 'multiline';
          const editorConfig = getCellEditorConfig(field, $projectStructureStore);
          const lookupValueConfig = getLookupValueGetterSetter(field, $projectStructureStore);
          const lookupRenderer = getLookupCellRenderer(field, $projectStructureStore);
          
          return {
            headerName: field.label || field.id,
            field: field.id,
            colId: field.id,
            pinned: undefined,
            width: fieldMultiline ? 200 : undefined,
            minWidth: fieldMultiline ? 200 : undefined,
            maxWidth: fieldMultiline ? 200 : undefined,
            suppressSizeToFit: fieldMultiline,
            suppressAutoSize: fieldMultiline,
            resizable: false,
            sortable: false,
            filter: false,
            lockPosition: false,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
            wrapText: fieldMultiline,
            autoHeight: false,
            ...editorConfig,
            valueGetter: (params: any) => {
              const fieldName = params.colDef.field;
              const value = params.data[fieldName];
              if (value === '-' || value === undefined || value === null) return '';
              
              // Use lookup value getter if available
              if (lookupValueConfig?.valueGetter) {
                return lookupValueConfig.valueGetter(params);
              }
              
              return value;
            },
            valueSetter: (params: any) => {
              const fieldName = params.colDef.field;
              let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
              
              // Use lookup value setter if available
              if (lookupValueConfig?.valueSetter) {
                return lookupValueConfig.valueSetter(params);
              }
              
              params.data[fieldName] = value;
              return true;
            },
            cellStyle: fieldMultiline ? {
              'white-space': 'normal',
              'word-wrap': 'break-word',
              'overflow-wrap': 'break-word',
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top',
              'min-height': '60px',
              'height': 'auto'
            } : {
              'padding': '8px 8px',
              'line-height': 'normal',
              'vertical-align': 'top'
            },
            cellRenderer: (params: any) => {
              let displayValue = params.value || '-';
              // For lookup fields, display the label instead of the ID
              if (lookupRenderer && displayValue !== '-') {
                displayValue = lookupRenderer(params) || '-';
              }
              const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
              return createCellWithIndicator(displayValue, sourceInfo, currentDocumentID);
            }
          };
        });

        if (groupColumns.length > 0) {
          columnDefs.push({
            headerName: column.label || column.id,
            marryChildren: true,
            children: groupColumns,
            resizable: false,
            lockPosition: false,
            suppressHeaderMenuButton: true,
            suppressHeaderContextMenu: true,
          });
        }
      } else {
        // Handle regular columns
        const editorConfig = getCellEditorConfig(column, $projectStructureStore);
        
        // For lookup columns, get value getter/setter configuration
        const lookupValueConfig = getLookupValueGetterSetter(column, $projectStructureStore);
        
        columnDefs.push({
          headerName: column.label || column.id,
          field: column.id,
          colId: column.id,
          pinned: isFixed ? 'left' : undefined,
          width: isMultiline ? 200 : (isFixed ? 200 : undefined),
          minWidth: isMultiline ? 200 : (isFixed ? 200 : undefined),
          maxWidth: isMultiline ? 200 : (isFixed ? 200 : undefined),
          suppressSizeToFit: isMultiline || isFixed,
          suppressAutoSize: isMultiline || isFixed,
          resizable: false,
          sortable: false,
          filter: false,
          lockPosition: true,
          suppressHeaderMenuButton: true,
          suppressHeaderContextMenu: true,
          wrapText: isMultiline, // Only wrap text for multiline columns
          // Disable autoHeight on columns - use getRowHeight instead to prevent flashing
          autoHeight: false, // Disable column-level autoHeight - row height calculated by getRowHeight
          ...editorConfig,
          // Convert "-" to "" for editing, "" to "-" for display
          // For lookup columns, use the shared utility's valueGetter/valueSetter
          valueGetter: (params: any) => {
            const field = params.colDef.field;
            const value = params.data[field];
            if (value === '-' || value === undefined || value === null) return '';
            
            // Use lookup value getter if available
            if (lookupValueConfig?.valueGetter) {
              return lookupValueConfig.valueGetter(params);
            }
            
            return value;
          },
          valueSetter: (params: any) => {
            const field = params.colDef.field;
            let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
            
            // Use lookup value setter if available
            if (lookupValueConfig?.valueSetter) {
              return lookupValueConfig.valueSetter(params);
            }
            
            params.data[field] = value;
            return true;
          },
          cellStyle: isMultiline ? {
            'white-space': 'normal',
            'word-wrap': 'break-word',
            'overflow-wrap': 'break-word',
            'padding': '8px 8px',
            'line-height': 'normal',
            'vertical-align': 'top',
            'min-height': '60px', // Fixed min height for multiline cells
            'height': 'auto' // Allow height to grow but not trigger recalculation
          } : {
            'padding': '8px 8px',
            'line-height': 'normal',
            'vertical-align': 'top'
          },
          cellRenderer: (params: any) => {
            let displayValue = params.value || '-';
            // For lookup fields, display the label instead of the ID
            const lookupRenderer = getLookupCellRenderer(column, $projectStructureStore);
            if (lookupRenderer && displayValue !== '-') {
              displayValue = lookupRenderer(params) || '-';
            }
            const sourceInfo = params.data?.[`${params.colDef.field}_sourceInfo`];
            return createCellWithIndicator(displayValue, sourceInfo, currentDocumentID);
          }
        });
      }
    });
    
    return columnDefs;
  }

  function createReferenceRowData(rows: any[], columns: any[] = []) {
    
    return rows.map((row) => {
      const rowData: any = {
        id: row.id // CRITICAL: Set row ID so AG Grid can properly identify and update rows
      };
      
      // If we have column structure, ensure all columns are represented
      if (columns.length > 0) {
        columns.forEach((column: any) => {
          // Handle grouped columns (e.g., study_design with fields: blinding, stratification_factors)
          if (column.type === 'group' && Array.isArray(column.fields)) {
            // For grouped columns, map cells by field IDs
            column.fields.forEach((field: any) => {
              const cell = row.cells?.find((c: any) => c.id === field.id);
              rowData[field.id] = cell?.value ?? '';
              // Store sourceInfo if available
              if (cell?.sourceInfo) {
                rowData[`${field.id}_sourceInfo`] = cell.sourceInfo;
              }
            });
          } else {
            // Handle regular columns
            const cell = row.cells?.find((c: any) => c.id === column.id);
            rowData[column.id] = cell?.value ?? '';
            // Store sourceInfo if available
            if (cell?.sourceInfo) {
              rowData[`${column.id}_sourceInfo`] = cell.sourceInfo;
            }
          }
        });
      } else {
        // Fallback: map only existing cells
        if (row.cells) {
          row.cells.forEach((cell: any) => {
            rowData[cell.id] = cell.value || '-';
            // Store sourceInfo if available
            if (cell.sourceInfo) {
              rowData[`${cell.id}_sourceInfo`] = cell.sourceInfo;
            }
          });
        }
      }
      
      return rowData;
    });
  }

function getNumericOrderValue(row: any, fallbackIndex: number): number {
  if (typeof row?.order === 'number' && !Number.isNaN(row.order)) {
    return row.order;
  }
  const parsed = parseInt(row?.order ?? '', 10);
  if (!Number.isNaN(parsed)) {
    return parsed;
  }
  return fallbackIndex;
}

function getNumericRowValue(row: any, fallbackIndex: number): number {
  // Alias for backward compatibility in function names only
  return getNumericOrderValue(row, fallbackIndex);
}

function sortRowsByRowField(rows: any[]): any[] {
  return [...(rows || [])].sort((a: any, b: any) => {
    const aIndex = rows.indexOf(a) + 1;
    const bIndex = rows.indexOf(b) + 1;
    const aRow = getNumericRowValue(a, aIndex);
    const bRow = getNumericRowValue(b, bIndex);
    if (aRow !== bRow) {
      return aRow - bRow;
    }
    return (a?.id || '').localeCompare(b?.id || '');
  });
}

  // Helper to create multi-level row data with row spanning
  function createMultiLevelRowData(
    rows: any[],
    columns: any[],
    tablesData?: any[],
    grouperStructure?: any
  ): any[] {
    const result: any[] = [];
    const sortedRows = sortRowsByRowField(rows || []);
    const levelDefinitions = Array.isArray(grouperStructure?.levels)
      ? grouperStructure.levels
      : [];

    sortedRows.forEach((row, index) => {
      const rowId = String(row?.grouperId ?? row?.id ?? `row_${index + 1}`);
      const rowNumber = getNumericRowValue(row, index + 1);
      const rowData: any = { id: rowId, row: rowNumber };

      levelDefinitions.forEach((levelDef: any, levelIndex: number) => {
        const levelKey = String(levelDef.level ?? levelDef.number ?? levelIndex + 1);
        const refs = getLevelRefsLocal(row, levelKey);
        const referenceDefs = levelDef.refDefs || [];

        referenceDefs.forEach((refDef: any, refIndex: number) => {
          const refId = refs[refIndex];
          const fieldName = `level${levelKey}_${refDef.refTable || refDef.columnId || refIndex}`;
          rowData[fieldName] = refId
            ? getReferenceValue(refDef.refTable, refId)
            : '-';
        });
      });

      const customValue = getCustomValueLocal(row);
      if (customValue) {
        rowData.custom = customValue;
      }

      result.push(rowData);
    });

    return result;
  }

  function createGrouperRowData(
    rows: any[],
    columns: any[],
    grouperRows?: any[],
    tablesData?: any[],
    isMultiLevel: boolean = false,
    grouperStructure?: any,
    study?: any
  ): any[] {
    const result: any[] = [];
    const sortedRows = sortRowsByRowField(rows || []);

    const levelEntries = Array.isArray(grouperStructure?.levels)
      ? grouperStructure.levels.map((level: any, index: number) => ({
          levelNumber: String(level.level ?? level.number ?? index + 1),
          label: level.label || level.id || `Level ${index + 1}`,
          sources: (level.sources || []).filter((source: any) => source?.type === 'reference')
        }))
      : [];

    sortedRows.forEach((grouperRow: any, index: number) => {
      if (!grouperRow) {
        return;
      }

      const rowId = String(grouperRow.grouperId ?? grouperRow.id ?? `row_${index + 1}`);
      const displayRowNumber = getNumericRowValue(grouperRow, index + 1);
      const customValue = getCustomValueLocal(grouperRow);

      const rowData: any = {
        id: rowId,
        row: displayRowNumber,
        rowId,
        group: '-',
        custom: customValue || '',
        levelDisplays: [] as string[]
      };

      // Read grouper cell values from cells array - ALWAYS read from data
      const grouperCells = Array.isArray(grouperRow?.cells) ? grouperRow.cells : [];
            
      // Set cell values directly from the cells array (data source of truth)
      grouperCells.forEach((cell: any) => {
        const cellValue = cell?.value;
        // Only set to '-' if value is explicitly empty/null/undefined, otherwise use the actual value
        const finalValue = (cellValue === null || cellValue === undefined || cellValue === '') ? '-' : cellValue;
        rowData[cell.id] = finalValue;
      });
      
      // Access projectStructure for validation/debugging only
      const currentProjectStructure = get(projectStructureStore) || {};
      const rootGrouperColumns = currentProjectStructure?.grouper?.columns || [];

      const levelDisplayMap = new Map<string, string>();

      levelEntries.forEach((levelEntry) => {
        const refs = getLevelRefsLocal(grouperRow, levelEntry.levelNumber);
        const displayParts = levelEntry.sources.map((source: any, refIndex: number) => {
          const refId = refs[refIndex];
          if (!refId) {
            return '';
          }
          return getReferenceValue(source.refTable, refId);
        }).filter((value: string) => value && value !== '-');

        const displayValue = displayParts.length > 0 ? displayParts.join(' ● ') : '-';
        rowData[`level_${levelEntry.levelNumber}`] = displayValue;
        levelDisplayMap.set(levelEntry.levelNumber, displayValue);
        if (displayValue && displayValue !== '-') {
          rowData.levelDisplays.push(displayValue);
        }
      });

      const primaryLevelKey = levelEntries[0]?.levelNumber;
      const primaryDisplay = primaryLevelKey ? levelDisplayMap.get(primaryLevelKey) : undefined;
      const groupParts: string[] = [];
      if (primaryDisplay && primaryDisplay !== '-') {
        groupParts.push(primaryDisplay);
      }
      if (customValue) {
        groupParts.push(customValue);
      }
      if (groupParts.length > 0) {
        rowData.group = groupParts.join(' ● ');
      } else if (primaryDisplay && primaryDisplay !== '-') {
        rowData.group = primaryDisplay;
      } else {
        // Show "Study" instead of study name when there are no references and no custom value
        rowData.group = 'Study';
      }

      result.push(rowData);
    });

    return result;
  }

/* TABLE EDITORS */
let showReferenceTableEditor = false;
let showTopicTableEditor = false;
let previousEditorStateForBlur = false; // Track previous state to detect closing
let currentPageNumber = 5; // Hardcoded for now, will come from PDF viewer
let currentTopicId = '';
let currentTableId = '';
let currentReferenceId = '';

function openDocumentEditor(topicId: string, tableId: string, referenceId: string = '') {
  if (referenceId) {
    // Reference mode
    currentReferenceId = referenceId;
    currentTopicId = '';
    currentTableId = '';
    showReferenceTableEditor = true;
    showTopicTableEditor = false;
  } else {
    // Topic mode (NOT for grouper - grouper uses separate editor)
    currentTopicId = topicId;
    currentTableId = tableId;
    currentReferenceId = '';
    showTopicTableEditor = true;
    showReferenceTableEditor = false;
  }
}

/* GROUPER EDITOR */
import GrouperEditor from './table-editor/GrouperEditor.svelte';
let showGrouperEditor = false;
let currentGrouperTopicId = '';

function openGrouperEditor(topicId: string) {
  currentGrouperTopicId = topicId;
  showGrouperEditor = true;
}

  function handleExpandAll(e: Event) {
    
    // Create new state with all sections expanded
    const newState: Record<string, boolean> = {};
    
    // Keep top-level sections expanded
    newState['section_references'] = true;
    newState['section_topics'] = true;
    
    // Expand all references
    (referenceSections || []).forEach((ref: any) => {
      newState[`ref_${ref.id}`] = true;
    });
    
    // Expand all topics and their tables
    (mergedTopics || []).forEach((topic: any) => {
      newState[`topic_${topic.id}`] = true;
      
      // Expand grouper table
      if (topic.grouper?.rows) {
        newState[`grouper_${topic.id}`] = true;
      }
      
      // Expand all data tables
      if (topic.tables?.data) {
        (topic.tables.data || []).forEach((table: any) => {
          newState[`table_${table.id}`] = true;
        });
      }
    });
    
    expandState = newState;
  }

  function handleCollapseAll(e: Event) {
    
    // Create new state based on "collapse all" logic
    // Top-level sections should be expanded
    const newState: Record<string, boolean> = {};
    newState['section_references'] = true;
    newState['section_topics'] = true;
    
    // Expand all topic sections
    (mergedTopics || []).forEach((topic: any) => {
      newState[`topic_${topic.id}`] = true;
    });
    
    // BUT collapse all tables (reference tables, grouper tables, and data tables)
    (referenceSections || []).forEach((ref: any) => {
      const refStructure = getReferenceStructure(ref.id);
      // Collapse both table and object types
      if (refStructure?.type === 'table' || refStructure?.type === 'object') {
        newState[`ref_${ref.id}`] = false;
      }
    });
    
    // Collapse all grouper and data tables
    (mergedTopics || []).forEach((topic: any) => {
      if (topic.grouper?.rows) {
        newState[`grouper_${topic.id}`] = false;
      }
      
      if (topic.tables?.data) {
        (topic.tables.data || []).forEach((table: any) => {
          newState[`table_${table.id}`] = false;
        });
      }
    });
    
    expandState = newState;
  }
</script>

<!-- Wrapper to enable absolute positioning of overlay -->
<div style="position: relative; width: 100%; height: 100%;">
  <!-- Normal ExtractItems2 content (always rendered) -->
  <div class="card-container extract-content" class:blurred={showReferenceTableEditor || showTopicTableEditor}>
   
    <!-- Tool Controls -->
    <div class="tool-controls">       
      <div class="tool-group">
        <div class="tool-buttons">
          <Button class="tool-button with-text" size="xs" color="light" title="Expand All" on:click={(e) => handleExpandAll(e)}><FontAwesomeIcon icon={faChevronDown} class="w-4 h-4" /> Expand</Button>
          <Button class="tool-button with-text" size="xs" color="light" title="Collapse All" on:click={(e) => handleCollapseAll(e)}><FontAwesomeIcon icon={faChevronUp} class="w-4 h-4" /> Collapse</Button>
        </div>
      </div>
      <div class="tool-group">
        <div class="tool-buttons" style="display: flex; align-items: center; gap: 0.5rem;">
          <span class="tool-toggle-label">Indicator:</span>
          <Toggle class="tool-toggle" color="green" bind:checked={showIndicators} size="small" />
        </div>
      </div>
      <div class="tool-group tool-group--right">
        <Button class="tool-button with-text" size="xs" color="light" title="Reset demo data" on:click={handleReset}>
          Reset
        </Button>
      </div>
    </div>

    <div class="extract-accordion-container">
   
      <!-- REFERENCES Top-Level Section -->
      <div class="accordion-section top-level">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <span class="accordion-title">
          <span class="accordion-item">
            <span class="accordion-button" on:click={() => toggle('section_references')}>
              {#if expandState['section_references']}
                <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 text-primary-50" />
              {:else}
                <FontAwesomeIcon icon={faChevronRight} class="w-3 h-3 text-primary-50" />
              {/if}
            </span>
            <span class="title text-primary-50">References</span>
          </span>
        </span>
        {#if expandState['section_references']}
          <div class="section-content">
            {#each referenceSections as reference (reference.id)}
              {@const refStructure = getReferenceStructure(reference.id)}
              <div class="accordion-section sub-section">
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <span class="accordion-item table-title">
                  <span class="accordion-button" on:click={() => toggle(`ref_${reference.id}`)}>
                    {#if expandState[`ref_${reference.id}`]}
                      <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 text-primary-50" />
                    {:else}
                      <FontAwesomeIcon icon={faChevronRight} class="w-3 h-3 text-primary-50" />
                    {/if}
                  </span> 
                  <span class="title">{getReferenceLabel(reference.id)}</span>            
                  {#if refStructure?.type === 'table'}
                    <Button pill={true} outline={false} class="general-button icon-only borderless" size="sm" on:click={() => openDocumentEditor('', '', reference.id)} title="Edit {getReferenceLabel(reference.id)}"> 
                      <FontAwesomeIcon icon={faPencil} />
                    </Button>
                  {/if}
                </span>
                {#if expandState[`ref_${reference.id}`]}
                  <div class="info-section">
                    {#if refStructure?.type === 'object'}
                      <!-- Object type - display as vertical table using AG Grid (keep old implementation for now) -->
                      <div class="ag-grid-container">
                        <div id="refGrid_{reference.id}" class="ag-theme-quartz ag-reference-grid ag-vertical-table" data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"></div>
                      </div>
                    {:else if refStructure?.type === 'table'}
                      <!-- Table type - display using ReferenceGrid component -->
                      <div class="ag-grid-container">
                        <ReferenceGrid
                          columns={refStructure.columns || refStructure.structure?.columns || []}
                          rows={reference.rows || []}
                          referenceId={reference.id}
                          showColumnKeys={false}
                          enableRowSelection={false}
                          domLayout="autoHeight"
                          showIndicators={showIndicators}
                        />
                      </div>
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>

      <!-- TOPICS Top-Level Section -->
      <div class="accordion-section top-level mb-4">
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <span class="accordion-title">
          <span class="accordion-item">
            <span class="accordion-button" on:click={() => toggle('section_topics')}>
              {#if expandState['section_topics']}
                <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 text-primary-50" />
              {:else}
                <FontAwesomeIcon icon={faChevronRight} class="w-3 h-3 text-primary-50" />
              {/if}
            </span>
            <span class="title text-primary-50">Topics</span>
          </span>
        </span>
        {#if expandState['section_topics']}
          <div class="section-content">
            {#each mergedTopics as topic (topic.id)}
              <div class="accordion-section sub-section">
                <!-- svelte-ignore a11y-click-events-have-key-events -->
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <span class="accordion-item table-title">
                  <span class="accordion-button" on:click={() => toggle(`topic_${topic.id}`)}>
                    {#if expandState[`topic_${topic.id}`]}
                      <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 ml-1 text-primary-50" />
                    {:else}
                      <FontAwesomeIcon icon={faChevronRight} class="w-3 h-3 ml-1 text-primary-50" />
                    {/if}
                  </span>
                  <span class="title text-primary-50">{topic.label}</span>
                </span>
                {#if expandState[`topic_${topic.id}`]}
                  <div class="extract-section info-section">
                    <!-- Display Grouper Table FIRST -->
                    {#if Array.isArray(topic.grouper)}
                      {@const grouperStructure = topic.grouperStructure}
                      {@const grouperData = topic.grouper}
                      <div class="table-section grouper-section">
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <span class="accordion-item table-title">
                          <span class="accordion-button" on:click={() => toggle(`grouper_${topic.id}`)}>
                            {#if expandState[`grouper_${topic.id}`]}
                              <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 text-primary-50" />
                            {:else}
                              <FontAwesomeIcon icon={faChevronRight} class="w-3 h-3 text-primary-50" />
                            {/if}
                          </span>
                          <span class="title">{grouperStructure?.label}</span>
                          <Button pill={true} outline={false} class="general-button icon-only borderless" size="sm" on:click={() => openGrouperEditor(topic.id)} title="Edit {grouperStructure?.label}"> 
                            <FontAwesomeIcon icon={faPencil} />
                          </Button>
                        </span>
                        {#if expandState[`grouper_${topic.id}`]}
                          <div class="ag-grid-container">
                            <div id="grouperGrid_{topic.id}" class="ag-theme-quartz ag-topic-grid" data-ag-theme-mode="{$theme === 'dark' ? 'dark' : 'light'}"></div>
                          </div>
                        {/if}
                      </div>
                    {/if}

                    <!-- Display Data Tables -->
                    {#if topic.tables?.data && topic.tables.data.length > 0}
                      {#each topic.tables.data as tableItem}
                        <div class="table-section">
                          <!-- svelte-ignore a11y-click-events-have-key-events -->
                          <!-- svelte-ignore a11y-no-static-element-interactions -->
                          <span class="accordion-item table-title">
                            <span class="accordion-button" on:click={() => toggle(`table_${tableItem.id}`)}>
                              {#if expandState[`table_${tableItem.id}`]}
                                <FontAwesomeIcon icon={faChevronDown} class="w-3 h-3 text-primary-50" />
                              {:else}
                                <FontAwesomeIcon icon={faChevronRight} class="w-3 h-3 text-primary-50" />
                              {/if}
                            </span>
                            <span class="title">{getTableStructure(topic, tableItem.id)?.label || tableItem.id}</span>
                            <Button pill={true} outline={false} class="general-button icon-only borderless" size="sm" on:click={() => openDocumentEditor(topic.id, tableItem.id)} title="Edit {getTableStructure(topic, tableItem.id)?.label || tableItem.id}"> 
                              <FontAwesomeIcon icon={faPencil} />
                            </Button>
                          </span>
                          {#if expandState[`table_${tableItem.id}`]}
                            {@const tableStructure = getTableStructure(topic, tableItem.id)}
                            {#if tableStructure}
                              <div class="ag-grid-container">
                                <TopicGrid
                                  columns={tableStructure.columns || []}
                                  rows={tableItem.rows || []}
                                  topicId={topic.id}
                                  tableId={tableItem.id}
                                  showColumnKeys={false}
                                  grouperData={Array.isArray(topic.grouper) ? topic.grouper : []}
                                  grouperStructure={topic.grouperStructure}
                                  showIndicators={showIndicators}
                                />
                              </div>
                            {/if}
                          {/if}
                        </div>
                      {/each}
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>

  <!-- Reference Table Editor overlay when open -->
  {#if showReferenceTableEditor}
    <div class="document-editor-overlay" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1000; background: rgba(0, 0, 0, 0.1);">
      <ReferenceTableEditor
        bind:show={showReferenceTableEditor}
        pageNumber={currentPageNumber}
        topicId={currentTopicId}
        tableId={currentTableId}
        referenceId={currentReferenceId}
      />
    </div>
  {/if}

  <!-- Topic Table Editor overlay when open -->
  {#if showTopicTableEditor}
    <div class="document-editor-overlay" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1000; background: rgba(0, 0, 0, 0.1);">
      <TopicTableEditor
        bind:show={showTopicTableEditor}
        pageNumber={currentPageNumber}
        topicId={currentTopicId}
        tableId={currentTableId}
        referenceId={currentReferenceId}
      />
    </div>
  {/if}

  <!-- GrouperEditor overlay when open -->
  {#if showGrouperEditor}
    <div class="document-editor-overlay" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 1000; background: rgba(0, 0, 0, 0.1);">
      <GrouperEditor
        bind:show={showGrouperEditor}
        topicId={currentGrouperTopicId}
      />
    </div>
  {/if}
</div>